import { HugeiconsIcon } from '@hugeicons/react';
import { LayoutGridIcon, LeftToRightListDashIcon } from '@hugeicons/core-free-icons';

import type { ProjectsViewMode } from '../../hooks/useProjectsViewMode';

interface ProjectsViewToggleProps {
  value: ProjectsViewMode;
  onChange: (next: ProjectsViewMode) => void;
}

interface ToggleButtonProps {
  active: boolean;
  label: string;
  onClick: () => void;
  icon: typeof LayoutGridIcon;
  side: 'left' | 'right';
}

function ToggleButton({ active, label, onClick, icon, side }: ToggleButtonProps) {
  const rounded = side === 'left' ? 'rounded-l-md' : 'rounded-r-md';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onClick={onClick}
      className={`${rounded} flex items-center justify-center w-[var(--oq-control-app-toolbar-height)] h-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-300 ${
        active
          ? 'bg-primary-50 text-primary-600'
          : 'bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      <HugeiconsIcon
        icon={icon}
        size={16}
        strokeWidth={2}
        color="currentColor"
        className="w-4 h-4 pointer-events-none"
        aria-hidden="true"
      />
    </button>
  );
}

export function ProjectsViewToggle({ value, onChange }: ProjectsViewToggleProps) {
  const handle = (next: ProjectsViewMode) => () => {
    if (next === value) return;
    onChange(next);
  };

  return (
    <div
      role="group"
      aria-label="Project view"
      className="inline-flex h-[var(--oq-control-app-toolbar-height)] shrink-0 items-stretch rounded-md border border-gray-200 overflow-hidden divide-x divide-gray-200"
    >
      <ToggleButton
        active={value === 'grid'}
        label="Grid view"
        onClick={handle('grid')}
        icon={LayoutGridIcon}
        side="left"
      />
      <ToggleButton
        active={value === 'table'}
        label="Table view"
        onClick={handle('table')}
        icon={LeftToRightListDashIcon}
        side="right"
      />
    </div>
  );
}
